import { Injectable } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';

import { SupabaseService } from '../../../core/services/supabase.service';
import { Tables } from '../../../core/supabase/database.types';
import { TasksRepository } from '../domain/tasks.repository';
import { TodoInput, TodoSubtask, TodoTask } from '../models/todo.model';

type TodoOccurrenceRow = Pick<
  Tables<'task_occurrences'>,
  | 'id'
  | 'task_definition_id'
  | 'scheduled_for'
  | 'status'
  | 'completed_at'
  | 'closed_at'
  | 'cancellation_reason'
  | 'created_at'
>;

type TodoSubtaskRow = Pick<
  Tables<'task_occurrence_subtasks'>,
  'id' | 'occurrence_id' | 'title_snapshot' | 'position' | 'is_completed' | 'completed_at'
>;

type TodoStatus = TodoOccurrenceRow['status'];

@Injectable()
export class SupabaseTasksRepository extends TasksRepository {
  // Generated Supabase types require owner_id on inserts and mark nullable SQL RPC
  // parameters as non-null. The database owns those invariants, so this adapter keeps
  // the SDK client broad while its mapped rows remain explicitly typed.
  private readonly client: SupabaseClient;

  constructor(supabase: SupabaseService) {
    super();
    this.client = supabase.client;
  }

  getTodos(): Promise<TodoTask[]> {
    return this.loadTodos(['OPEN', 'COMPLETED', 'CANCELLED'], true);
  }

  getOpenTodos(): Promise<TodoTask[]> {
    return this.loadTodos(['OPEN'], true);
  }

  getClosedTodos(): Promise<TodoTask[]> {
    return this.loadTodos(['COMPLETED', 'CANCELLED'], false);
  }

  async createTodo(input: TodoInput): Promise<void> {
    const definition = await this.insertDefinition(input);
    try {
      const occurrence = await this.insertOccurrence(definition.id, input.scheduledFor);
      await this.replaceSubtasks(definition.id, occurrence.id, input.subtasks);
    } catch (error) {
      await this.client.from('task_definitions').delete().eq('id', definition.id);
      throw error;
    }
  }

  async updateTodo(task: TodoTask, input: TodoInput): Promise<void> {
    const { error: definitionError } = await this.client
      .from('task_definitions')
      .update({
        title: input.title,
        description: input.description,
        priority: input.priority
      })
      .eq('id', task.definitionId);
    if (definitionError) throw definitionError;

    const scheduleChanged = task.scheduledFor !== input.scheduledFor;
    const { error: occurrenceError } = await this.client
      .from('task_occurrences')
      .update({
        scheduled_for: input.scheduledFor,
        original_scheduled_for: input.scheduledFor,
        rollover_days: 0,
        rescheduled_at: scheduleChanged ? new Date().toISOString() : null
      })
      .eq('id', task.occurrenceId)
      .eq('status', 'OPEN');
    if (occurrenceError) throw occurrenceError;

    await this.syncSubtasks(task, input.subtasks);
  }

  async completeTodo(task: TodoTask): Promise<void> {
    const now = new Date().toISOString();
    const { error: subtaskError } = await this.client
      .from('task_occurrence_subtasks')
      .update({ is_completed: true, completed_at: now })
      .eq('occurrence_id', task.occurrenceId)
      .eq('is_completed', false);
    if (subtaskError) throw subtaskError;

    const { error } = await this.client
      .from('task_occurrences')
      .update({ status: 'COMPLETED', completed_at: now, closed_at: now })
      .eq('id', task.occurrenceId)
      .eq('status', 'OPEN');
    if (error) throw error;
  }

  async cancelTodo(task: TodoTask, reason: string | null): Promise<void> {
    const now = new Date().toISOString();
    const { error } = await this.client
      .from('task_occurrences')
      .update({
        status: 'CANCELLED',
        completed_at: null,
        closed_at: now,
        cancellation_reason: reason?.trim() || null
      })
      .eq('id', task.occurrenceId)
      .eq('status', 'OPEN');
    if (error) throw error;
  }

  async reopenTodo(task: TodoTask): Promise<void> {
    const { error: subtaskError } = await this.client
      .from('task_occurrence_subtasks')
      .update({ is_completed: false, completed_at: null })
      .eq('occurrence_id', task.occurrenceId);
    if (subtaskError) throw subtaskError;

    const { error } = await this.client
      .from('task_occurrences')
      .update({
        status: 'OPEN',
        completed_at: null,
        closed_at: null,
        cancellation_reason: null
      })
      .eq('id', task.occurrenceId)
      .in('status', ['COMPLETED', 'CANCELLED']);
    if (error) throw error;
  }

  async deleteTodo(task: TodoTask): Promise<void> {
    await this.deleteDefinitions([task.definitionId]);
  }

  async deleteClosedTodos(tasks: TodoTask[]): Promise<void> {
    const definitionIds = tasks
      .filter(task => task.status === 'COMPLETED' || task.status === 'CANCELLED')
      .map(task => task.definitionId);
    await this.deleteDefinitions(definitionIds);
  }

  async rescheduleTodo(task: TodoTask, scheduledFor: string | null): Promise<void> {
    const { error } = await this.client
      .from('task_occurrences')
      .update({
        scheduled_for: scheduledFor,
        original_scheduled_for: scheduledFor,
        rollover_days: 0,
        rescheduled_at: new Date().toISOString()
      })
      .eq('id', task.occurrenceId)
      .eq('status', 'OPEN');
    if (error) throw error;
  }

  async setSubtaskCompleted(subtask: TodoSubtask, completed: boolean): Promise<void> {
    const { error } = await this.client
      .from('task_occurrence_subtasks')
      .update({
        is_completed: completed,
        completed_at: completed ? new Date().toISOString() : null
      })
      .eq('id', subtask.id);
    if (error) throw error;
  }

  private async loadTodos(statuses: TodoStatus[], includeSubtasks: boolean): Promise<TodoTask[]> {
    const { data: definitions, error: definitionsError } = await this.client
      .from('task_definitions')
      .select('id,title,description,priority')
      .eq('kind', 'TODO')
      .is('archived_at', null)
      .order('created_at', { ascending: false });

    if (definitionsError) throw definitionsError;
    if (!definitions?.length) return [];

    const definitionIds = definitions.map(definition => definition.id);
    const { data: occurrences, error: occurrencesError } = await this.client
      .from('task_occurrences')
      .select('id,task_definition_id,scheduled_for,status,completed_at,closed_at,cancellation_reason,created_at')
      .in('task_definition_id', definitionIds)
      .in('status', statuses)
      .order('created_at', { ascending: false });

    if (occurrencesError) throw occurrencesError;

    const latestByDefinition = new Map<number, TodoOccurrenceRow>();
    for (const occurrence of occurrences ?? []) {
      if (!latestByDefinition.has(occurrence.task_definition_id)) {
        latestByDefinition.set(occurrence.task_definition_id, occurrence);
      }
    }

    let subtaskRows: TodoSubtaskRow[] = [];
    if (includeSubtasks) {
      const occurrenceIds = [...latestByDefinition.values()].map(occurrence => occurrence.id);
      if (occurrenceIds.length) {
        const { data, error } = await this.client
          .from('task_occurrence_subtasks')
          .select('id,occurrence_id,title_snapshot,position,is_completed,completed_at')
          .in('occurrence_id', occurrenceIds)
          .order('position');
        if (error) throw error;
        subtaskRows = data ?? [];
      }
    }

    return definitions
      .map(definition => {
        const occurrence = latestByDefinition.get(definition.id);
        if (!occurrence) return null;
        const subtasks: TodoSubtask[] = subtaskRows
          .filter(row => row.occurrence_id === occurrence.id)
          .map(row => ({
            id: row.id,
            title: row.title_snapshot,
            position: row.position,
            isCompleted: row.is_completed,
            completedAt: row.completed_at
          }));

        return {
          definitionId: definition.id,
          occurrenceId: occurrence.id,
          title: definition.title,
          description: definition.description,
          priority: definition.priority,
          scheduledFor: occurrence.scheduled_for,
          status: occurrence.status,
          completedAt: occurrence.completed_at,
          closedAt: occurrence.closed_at,
          cancellationReason: occurrence.cancellation_reason,
          subtasks
        } satisfies TodoTask;
      })
      .filter((task): task is TodoTask => task !== null);
  }

  private async syncSubtasks(task: TodoTask, titles: string[]): Promise<void> {
    const cleanedTitles = titles.map(title => title.trim()).filter(Boolean);
    const usedIds = new Set<number>();

    const desired = cleanedTitles.map((title, position) => {
      const exactMatch = task.subtasks.find(
        subtask => !usedIds.has(subtask.id) && subtask.title === title
      );
      const positionalMatch = task.subtasks[position];
      const existing = exactMatch
        ?? (positionalMatch && !usedIds.has(positionalMatch.id) ? positionalMatch : null);

      if (existing) usedIds.add(existing.id);
      return { id: existing?.id ?? null, title, position };
    });

    const { data: currentRows, error: currentError } = await this.client
      .from('task_occurrence_subtasks')
      .select('id')
      .eq('occurrence_id', task.occurrenceId);
    if (currentError) throw currentError;

    const retainedIds = new Set(
      desired.flatMap(item => item.id === null ? [] : [item.id])
    );
    const removedIds = (currentRows ?? [])
      .map(row => row.id)
      .filter(id => !retainedIds.has(id));

    if (removedIds.length) {
      const { error } = await this.client
        .from('task_occurrence_subtasks')
        .delete()
        .in('id', removedIds);
      if (error) throw error;
    }

    await Promise.all(
      desired
        .filter((item): item is { id: number; title: string; position: number } => item.id !== null)
        .map(async item => {
          const { error } = await this.client
            .from('task_occurrence_subtasks')
            .update({ title_snapshot: item.title, position: item.position })
            .eq('id', item.id);
          if (error) throw error;
        })
    );

    const newRows = desired.filter(item => item.id === null);
    if (newRows.length) {
      const { error } = await this.client
        .from('task_occurrence_subtasks')
        .insert(newRows.map(item => ({
          occurrence_id: task.occurrenceId,
          template_id: null,
          title_snapshot: item.title,
          position: item.position
        })));
      if (error) throw error;
    }

    await this.replaceTemplates(task.definitionId, cleanedTitles);
  }

  private async deleteDefinitions(definitionIds: number[]): Promise<void> {
    if (!definitionIds.length) return;

    const { error } = await this.client
      .from('task_definitions')
      .delete()
      .in('id', definitionIds);
    if (error) throw error;
  }

  private async insertDefinition(input: TodoInput): Promise<{ id: number }> {
    const { data, error } = await this.client
      .from('task_definitions')
      .insert({
        kind: 'TODO',
        title: input.title,
        description: input.description,
        priority: input.priority
      })
      .select('id')
      .single();
    if (error) throw error;
    return data;
  }

  private async insertOccurrence(definitionId: number, scheduledFor: string | null): Promise<{ id: number }> {
    const { data, error } = await this.client
      .from('task_occurrences')
      .insert({
        task_definition_id: definitionId,
        task_kind: 'TODO',
        scheduled_for: scheduledFor,
        original_scheduled_for: scheduledFor,
        status: 'OPEN'
      })
      .select('id')
      .single();
    if (error) throw error;
    return data;
  }

  private async replaceSubtasks(
    definitionId: number,
    occurrenceId: number,
    titles: string[]
  ): Promise<void> {
    const cleanedTitles = titles.map(title => title.trim()).filter(Boolean);
    await this.replaceTemplates(definitionId, cleanedTitles);

    if (!cleanedTitles.length) return;

    const { error: occurrenceError } = await this.client
      .from('task_occurrence_subtasks')
      .insert(cleanedTitles.map((title, position) => ({
        occurrence_id: occurrenceId,
        template_id: null,
        title_snapshot: title,
        position
      })));
    if (occurrenceError) throw occurrenceError;
  }

  private async replaceTemplates(definitionId: number, cleanedTitles: string[]): Promise<void> {
    const { error: templateDeleteError } = await this.client
      .from('task_subtask_templates')
      .delete()
      .eq('task_definition_id', definitionId);
    if (templateDeleteError) throw templateDeleteError;

    if (!cleanedTitles.length) return;

    const { error: templateError } = await this.client
      .from('task_subtask_templates')
      .insert(cleanedTitles.map((title, position) => ({
        task_definition_id: definitionId,
        title,
        position
      })));
    if (templateError) throw templateError;
  }
}
